import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useFetchData from '../../../../../hooks/useFetchData'
import UserFormSkeleton from '../../../../skeleton/UserFormSkeleton'
import ErrorBoundary from '../../../../ErrorBoundary/ErrorBoundary'
import Module from '../../../../layout/Module'
import Typography from '@mui/material/Typography'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import { useDispatch, useSelector } from 'react-redux'
import useUsers from '../../../../../hooks/users/useUsers'
import Alert from '@mui/material/Alert'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import Dialog from '../../../../dialog/Dialog'
import DialogAction from '../Dialog/Dialog'
import { toggleAlertModule } from '../../../../../store/actions/alert.actions'

const EditUser = () => {
  const permission = useSelector((state) => state.session?.permissions?.user || {})
  const userIdSession = useSelector((state) => state?.session?.user?.userId)
  const domains = useSelector(
    (state) => state?.tenant?.sso?.filter((sso) => sso.isEnabled).map((sso) => sso.domain) || []
  )
  const navigate = useNavigate()
  const { id } = useParams()

  const { isLoading, data, error } = useFetchData(`user/${id}`)
  const [form, setInputForm] = useState({})
  const [errors, setInputErrors] = useState({})
  const [loading, toggleLoading] = useState(false)
  const [show, setShow] = useState(false)
  const [modal, setModal] = useState(null)
  const alertSave = useSelector((state) => state?.alert?.save || {})
  const dispatch = useDispatch()
  const { isLoading: isLoadingList, updateUser } = useUsers()

  const onChange = (name, value) => {
    if (name === 'flag') {
      setInputForm({ ...form, data: { ...form.data, flag: value } })
    } else if (name === 'rolesId') {
      const { rolesId, roles } = value
      setInputForm({ ...form, rolesId, roles })
      setInputErrors({ ...errors, [name]: '' })
    } else {
      setInputForm({ ...form, [name]: value })
      setInputErrors({ ...errors, [name]: '' })
    }
    if (!alertSave.mode || !alertSave.onSave) {
      dispatch(toggleAlertModule(false, 'Users', 'edit', onSubmit, alertSave.onDontSave))
    }
  }

  useEffect(() => {
    if (data) {
      const rolesId = data?.roles?.map((role) => role.id) || []
      const yearLevelId = data?.yearLevel?.map((year) => year._id) || []
      const formData = {
        ...(data?.data || {}),
        ...(data || {}),
        rolesId,
        yearLevelId
      }

      setInputForm(formData || {})
    }
  }, [data])
  const onCancel = () => {
    navigate(`/dashboard/users`)
  }
  const onSubmit = async (e) => {
    e.preventDefault()
    toggleLoading(true)
    setInputErrors({})
    try {
      let result = await updateUser(id, form)
      if (result.error) {
        setInputErrors(result)
      }
    } finally {
      toggleLoading(false)
    }
  }

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary justify-between'>
        <Typography variant='subtitle1' className='text-primary'>
          Edit user
        </Typography>
        <div className='flex items-center justify-end gap-2'>
          {permission.archived && (
            <IconTooltipButton
              id={`archive-${id}`}
              disabled={isLoading || loading || isLoadingList}
              title='Archive'
              icon='archive'
              style={{
                borderRadius: '0.5rem',
                backgroundColor: 'var(--destructive)',
                color: '#fff',
                padding: '0.45rem'
              }}
              className='shadow-sm'
              onClick={() => setModal({ type: 'archive', data })}
            />
          )}
          {permission.deleted && (
            <IconTooltipButton
              id={`delete-${id}`}
              disabled={isLoading || loading || isLoadingList}
              title='Delete'
              icon='trash-alt'
              style={{
                borderRadius: '0.5rem',
                backgroundColor: '#fff',
                color: 'var(--destructive)',
                padding: '0.45rem'
              }}
              className='shadow-sm'
              onClick={() => setModal({ type: 'delete', data })}
            />
          )}
          <div className='px-4' />
          {permission.preview && (
            <IconTooltipButton
              disabled={isLoading || loading || isLoadingList}
              title='Preview'
              icon='expand-alt'
              style={{
                borderRadius: '0.5rem',
                backgroundColor: 'var(--primary)',
                color: 'var(--primary-foreground)',
                padding: '0.45rem'
              }}
              className='shadow-sm'
              onClick={() => setShow(true)}
            />
          )}
          {domains.includes(data?.email?.split('@')[1]) &&
          !data?.roles?.some((role) => role.name === 'Parent') ? (
            <></>
          ) : data?.activated ? (
            permission['recovery-password'] && (
              <IconTooltipButton
                disabled={isLoading || loading || isLoadingList}
                title='Recovery password'
                icon='key'
                style={{
                  borderRadius: '0.5rem',
                  backgroundColor: 'var(--muted)',
                  color: 'var(--muted-foreground)',
                  padding: '0.45rem'
                }}
                className='shadow-sm'
                onClick={() => setModal({ type: 'recovery password', data })}
              />
            )
          ) : (
            permission['send-invite'] && (
              <IconTooltipButton
                disabled={isLoading || loading || isLoadingList}
                title='Send invitation'
                icon='paper-plane'
                style={{
                  borderRadius: '0.5rem',
                  backgroundColor: 'var(--muted)',
                  color: data?.invited === 0 ? 'var(--secondary)' : 'var(--accent)',
                  padding: '0.45rem'
                }}
                className='shadow-sm'
                onClick={() => setModal({ type: 'send invitation', data })}
              />
            )
          )}
          {permission['login-impersonate'] && String(id) !== String(userIdSession) && (
            <IconTooltipButton
              disabled={isLoading || loading || isLoadingList}
              title='Login impersonate'
              icon='sign-in'
              style={{
                borderRadius: '0.5rem',
                backgroundColor: '#fff',
                color: 'var(--primary)',
                padding: '0.45rem 0.4rem'
              }}
              className='shadow-sm'
              onClick={() => setModal({ type: 'login impersonate', data })}
            />
          )}
          <div className='px-2' />
          <AlertSaveButton
            submodule='Users'
            disabled={isLoading || loading || isLoadingList}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm'
            onDontSave={onCancel}
          />
          {permission.updated && (
            <ButtonStartIcon
              disabled={isLoading || loading || isLoadingList}
              loading={loading}
              onClick={onSubmit}
              startIcon={['fal', 'check']}
              color='primary'
            >
              Save
            </ButtonStartIcon>
          )}
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer classNameWrapper='overflow-auto'>
        {errors && errors.message && (
          <Alert severity='error' className='mb-4' onClose={() => setInputErrors({ message: null })}>
            {errors.message}
          </Alert>
        )}
        {isLoading ? (
          <UserFormSkeleton isLoading={isLoading} />
        ) : error ? (
          <ErrorBoundary />
        ) : (
          <UserFormSkeleton
            isLoading={isLoading}
            isLoadingForm={loading || isLoadingList}
            form={form}
            isPreview={!permission.updated.includes('basic-information')}
            onChange={onChange}
            errors={errors}
            permission={permission.updated}
          />
        )}
      </Module.BodyContainer>
      <Dialog
        id={`user-preview-${id}`}
        show={show}
        title='user preview'
        onClose={() => setShow(false)}
        onCloseHeader={true}
        maxWidth='md'
      >
        <UserFormSkeleton isLoading={loading} form={form} />
      </Dialog>
      {modal && (
        <DialogAction show={!!modal} onClose={() => setModal(null)} type={modal.type} form={modal.data} />
      )}
    </>
  )
}

export default EditUser
